import { Logo } from "@/components/Icons/Logo";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { getI18nProps } from "@/utils/i18n";

export const getStaticProps = getI18nProps;
const AVAILABLE_PAGES = {
  blog: true,
  careers: false,
  docs: true,
};

const linkClass = "text-sm text-gray-600 hover:text-gray-900";

export const Footer = () => {
  const t = useTranslations("footer");
  const router = useRouter();
  const locale = router.locale ?? router.defaultLocale;
  const integrationLinks = [
    { href: "/shopify-integration", label: t("links.shopifyIntegration") },
    {
      href: "/wordpress-woocommerce-integration",
      label: t("links.wordpressIntegration"),
    },
    { href: "/stripe-integration", label: t("links.stripeIntegration") },
    { href: "/custom-api-integration", label: t("links.customApiIntegration") },
    { href: "/manual-integration", label: t("links.manualIntegration") },
    { href: "/horoshop-integration", label: t("links.horoshopIntegration") },
  ];

  const InternalLink = ({ href, children }) => (
    <Link
      href={href}
      locale={
        href === "/shopify-guides" ||
        href === "/affiliate-commission-calculator"
          ? "en"
          : locale
      }
      className={linkClass}
    >
      {children}
    </Link>
  );

  return (
    <footer className="border-t border-gray-300 bg-gray-100">
      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8 lg:py-20">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          {/* Logo & mission */}
          <div className="space-y-6 xl:col-span-1">
            <Logo className="h-8 w-auto text-gray-900" />

            <p className="text-lg font-semibold leading-6 text-gray-900">
              {t("mission.tagline")}
            </p>

            <p className="text-sm leading-6 text-gray-600">
              {t("mission.subtitle")}
            </p>

            {/* Social */}
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/afivio_com"
                target="_blank"
                className="text-gray-400 hover:text-gray-600"
                rel="noreferrer"
              >
                <Instagram className="h-5 w-5" />
              </a>

              <a
                href="https://www.facebook.com/profile.php?id=61579156503707"
                target="_blank"
                className="text-gray-400 hover:text-gray-600"
                rel="noreferrer"
              >
                <Facebook className="h-5 w-5" />
              </a>

              <a
                href="https://www.linkedin.com/company/afivio"
                target="_blank"
                className="text-gray-400 hover:text-gray-600"
                rel="noreferrer"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-12 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
            {/* Product + Company */}
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="mb-4 text-sm font-semibold text-gray-900">
                  {t("sections.product")}
                </h3>

                <ul className="space-y-3">
                  {/* <li><InternalLink href="/features">{t('links.features')}</InternalLink></li> */}
                  <li>
                    <InternalLink href="/pricing">
                      {t("links.pricing")}
                    </InternalLink>
                  </li>

                  {/* External (affiliate site) */}
                  <li>
                    <a
                      href={`${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/signup`}
                      className={linkClass}
                    >
                      {t("links.forAffiliates")}
                    </a>
                  </li>

                  <li>
                    <InternalLink href="/affiliate-marketing">
                      {t("links.affiliateMarketing")}
                    </InternalLink>
                  </li>

                  <li>
                    <InternalLink href="/influencer-marketing">
                      {t("links.influencerMarketing")}
                    </InternalLink>
                  </li>

                  <li>
                    <InternalLink href="/referral-marketing">
                      {t("links.referralMarketing")}
                    </InternalLink>
                  </li>
                </ul>
              </div>

              <div className="mt-6 md:mt-0">
                <h3 className="mb-4 text-sm font-semibold text-gray-900">
                  {t("sections.company")}
                </h3>

                <ul className="space-y-3">
                  <li>
                    <InternalLink href="/about">
                      {t("links.about")}
                    </InternalLink>
                  </li>

                  <li>
                    <InternalLink href="/faq">{t("links.faq")}</InternalLink>
                  </li>

                  {AVAILABLE_PAGES.blog && (
                    <li>
                      <InternalLink href="/blog">
                        {t("links.blog")}
                      </InternalLink>
                    </li>
                  )}

                  <li>
                    <InternalLink href="/partners">
                      {t("links.partners")}
                    </InternalLink>
                  </li>

                  <li>
                    <InternalLink href="/affiliate-program">
                      {t("links.afivioAffiliateProgram")}
                    </InternalLink>
                  </li>
                </ul>
              </div>
            </div>

            {/* Help + Legal + Integrations */}
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="mb-4 text-sm font-semibold text-gray-900">
                  {t("sections.help")}
                </h3>

                <ul className="space-y-3">
                  <li>
                    <InternalLink href="/how-it-works">
                      {t("links.howItWorks")}
                    </InternalLink>
                  </li>

                  <li>
                    <InternalLink href="/shopify-guides">
                      Shopify guides & tools
                    </InternalLink>
                  </li>

                  {/* {AVAILABLE_PAGES.docs && (
                    <li>
                      <a href="/ExternalDocs.html" className={linkClass}>
                        Documentation
                      </a>
                    </li>
                  )} */}

                  <li>
                    <InternalLink href="/contact">
                      {t("links.contact")}
                    </InternalLink>
                  </li>
                </ul>

                <div className="mt-8">
                  <h3 className="mb-4 text-sm font-semibold text-gray-900">
                    {t("sections.legal")}
                  </h3>

                  <ul className="space-y-3">
                    <li>
                      <InternalLink href="/terms">
                        {t("links.terms")}
                      </InternalLink>
                    </li>
                    <li>
                      <InternalLink href="/privacy">
                        {t("links.privacy")}
                      </InternalLink>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 md:mt-0">
                <h3 className="mb-4 text-sm font-semibold text-gray-900">
                  {t("sections.integrations")}
                </h3>

                <ul className="space-y-3">
                  {integrationLinks.map((item) => (
                    <li key={item.href}>
                      <InternalLink href={item.href}>{item.label}</InternalLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 border-t border-gray-200 pt-8">
          <p className="text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Afivio. {t("copyright.suffix")}
          </p>
          <p className="mt-1 text-center text-xs text-gray-500">
            <a href="mailto:contact@afivio.com" className="hover:text-gray-700">
              contact@afivio.com
            </a>
          </p>
        </div>
      </div>

      <div className="mt-8 border-t border-gray-200 pt-8">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SiteNavigationElement",
              name: t("schema.navigationName"),
              url: "https://afivio.com",
              mainEntity: {
                "@type": "ItemList",
                itemListElement: [
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.about"),
                    url: "https://afivio.com/about",
                    position: 1,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.howItWorks"),
                    url: "https://afivio.com/how-it-works",
                    position: 2,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.blog"),
                    url: "https://afivio.com/blog",
                    position: 3,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.pricing"),
                    url: "https://afivio.com/pricing",
                    position: 4,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.faq"),
                    url: "https://afivio.com/faq",
                    position: 5,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.shopifyIntegration"),
                    url: "https://afivio.com/shopify-integration",
                    position: 6,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.wordpressIntegration"),
                    url: "https://afivio.com/wordpress-woocommerce-integration",
                    position: 7,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.stripeIntegration"),
                    url: "https://afivio.com/stripe-integration",
                    position: 8,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.customApiIntegration"),
                    url: "https://afivio.com/custom-api-integration",
                    position: 9,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.manualIntegration"),
                    url: "https://afivio.com/manual-integration",
                    position: 10,
                  },
                  {
                    "@type": "SiteNavigationElement",
                    name: t("schema.items.horoshopIntegration"),
                    url: "https://afivio.com/horoshop-integration",
                    position: 11,
                  },
                ],
              },
            }),
          }}
        />
      </div>
    </footer>
  );
};

export default Footer;
